// src/AdminToolsPage.jsx  — catalogo v2
import React, { useCallback, useEffect, useState } from 'react';
import { fetchJson } from './api';
import CvCatalogAdmin from './CvCatalogAdmin';
import { useTranslation } from 'react-i18next';

const S = {
  page: {
    minHeight: '100vh',
    padding: '24px',
    background: 'radial-gradient(circle at 0% 0%, rgba(212,165,80,0.12), transparent 36%), #0b0d13',
    color: '#ecedf4',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  wrap: { maxWidth: '1280px', margin: '0 auto' },
  logo: { fontFamily: 'Kanit, sans-serif', fontSize: '13px', fontWeight: 800, letterSpacing: '0.2em', color: '#d4a550', marginBottom: '4px' },
  title: { fontSize: '28px', fontWeight: 700, marginBottom: '4px', letterSpacing: '-0.01em' },
  subtitle: { marginBottom: '28px', color: '#7a7f96', fontSize: '13px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '80px' },
  kpiLabel: { color: '#7a7f96', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '8px' },
  kpiValue: { fontSize: '26px', fontWeight: 800, color: '#d4a550' },
  section: { marginTop: '16px', background: 'rgba(17,19,28,0.8)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.07)', padding: '20px', marginBottom: '16px' },
  sectionTitle: { fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#f0ece4', fontFamily: 'Kanit, sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' },
  row: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' },
  input: { background: '#0e1018', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#ecedf4', padding: '9px 12px', fontSize: '13px', outline: 'none' },
  btn: { background: '#d4a550', border: 'none', borderRadius: '8px', padding: '9px 16px', color: '#0b0d13', fontWeight: 700, cursor: 'pointer', fontSize: '13px', fontFamily: 'Kanit, sans-serif', letterSpacing: '0.05em' },
  btnGhost: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 14px', color: '#b0adb8', cursor: 'pointer', fontSize: '13px' },
  btnDanger: { background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: '8px', padding: '8px 14px', color: '#ff8484', cursor: 'pointer', fontSize: '13px' },
  btnSuccess: { background: 'rgba(78,222,128,0.15)', border: '1px solid rgba(78,222,128,0.3)', borderRadius: '8px', padding: '8px 14px', color: '#67df95', cursor: 'pointer', fontSize: '13px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: '11px', color: '#7a7f96', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '10px 8px', textTransform: 'uppercase', letterSpacing: '0.08em' },
  td: { borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '10px 8px', fontSize: '13px', verticalAlign: 'middle' },
  small: { color: '#7a7f96', fontSize: '11px' },
  badge: (color) => ({
    display: 'inline-block', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 600,
    background: color === 'green' ? 'rgba(78,222,128,0.15)' : color === 'gold' ? 'rgba(212,165,80,0.15)' : color === 'red' ? 'rgba(255,80,80,0.15)' : 'rgba(120,169,240,0.15)',
    color: color === 'green' ? '#67df95' : color === 'gold' ? '#d4a550' : color === 'red' ? '#ff8484' : '#9cc0ff',
    border: `1px solid ${color === 'green' ? 'rgba(103,223,149,0.3)' : color === 'gold' ? 'rgba(212,165,80,0.3)' : color === 'red' ? 'rgba(255,80,80,0.3)' : 'rgba(156,192,255,0.3)'}`,
  }),
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0' },
  tab: (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: active ? 700 : 400,
    color: active ? '#d4a550' : '#7a7f96', background: 'transparent', border: 'none',
    borderBottom: active ? '2px solid #d4a550' : '2px solid transparent', fontFamily: 'Kanit, sans-serif',
    letterSpacing: '0.05em', textTransform: 'uppercase',
  }),
};

function Msg({ text, error }) {
  if (!text) return null;
  return <div style={{ marginTop: '12px', fontSize: '12px', color: error ? '#ff8484' : '#67df95' }}>{text}</div>;
}

export default function AdminToolsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [wakeupLoading, setWakeupLoading] = useState({});
  const [nlSubject, setNlSubject] = useState('');
  const [nlBody, setNlBody] = useState('');
  const [nlSegment, setNlSegment] = useState('all_freelance');
  const [nlTestEmail, setNlTestEmail] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [nlImageUrl, setNlImageUrl] = useState('');
  const [nlImageUploading, setNlImageUploading] = useState(false);
  const [nlResult, setNlResult] = useState(null);
  const [nlLog, setNlLog] = useState([]);

  // ── Catalogo v2 ──
  const [catQProgrammi, setCatQProgrammi] = useState('');
  const [catProgrammi, setCatProgrammi] = useState([]);
  const [catProgrammiPage, setCatProgrammiPage] = useState(1);
  const [catProgrammiPages, setCatProgrammiPages] = useState(1);
  const [catProgrammiTotal, setCatProgrammiTotal] = useState(0);
  const [catProgrammiLoading, setCatProgrammiLoading] = useState(false);

  // Quale programma è espanso (id o null) — un solo programma alla volta
  const [catExpandedProg, setCatExpandedProg] = useState(null);
  // Valori form edit programma corrente
  const [catEditProg, setCatEditProg] = useState({});
  // Messaggi feedback per programma { [progId]: { text, error } | null }
  const [catProgMsg, setCatProgMsg] = useState({});

  // Cache lazy edizioni per programma: { [program_id]: [...edizioni] }
  const [catEdizioniCache, setCatEdizioniCache] = useState({});
  // Loading per programma: { [program_id]: bool }
  const [catEdLoadingMap, setCatEdLoadingMap] = useState({});

  // Quale edizione è in edit (id o null)
  const [catExpandedEd, setCatExpandedEd] = useState(null);
  // Valori form edit edizione corrente
  const [catEditEd, setCatEditEd] = useState({});
  // Messaggi feedback per edizione { [edId]: { text, error } | null }
  const [catEdMsg, setCatEdMsg] = useState({});

  // Form "Aggiungi edizione": quale program_id ha il form aperto (null = chiuso)
  const [catNewEdProgId, setCatNewEdProgId] = useState(null);
  // Valori form nuova edizione
  const [catNewEdForm, setCatNewEdForm] = useState({ edition_label: '', year: '', window_start: '', window_end: '' });
  // Feedback form nuova edizione
  const [catNewEdMsg, setCatNewEdMsg] = useState(null);
  // Stato salvataggio nuova edizione
  const [catNewEdSaving, setCatNewEdSaving] = useState(false);

  async function sendNewsletter(test = false) {
    if (!nlSubject || !nlBody) return;
    setNlLoading(true); setNlResult(null);
    try {
      const body = { subject: nlSubject, body: nlBody, segment: nlSegment, image_url: nlImageUrl || undefined };
      if (test) body.test_email = nlTestEmail;
      const data = await fetchJson('/admin/newsletter/send', { method: 'POST', body: JSON.stringify(body) });
      setNlResult(data);
    } catch(e) { setNlResult({ error: e.message }); }
    finally { setNlLoading(false); }
  }
  const [wakeupResult, setWakeupResult] = useState({});
  const [testEmail, setTestEmail] = useState('');

  async function sendWakeup(type, test = false) {
    setWakeupLoading(p => ({ ...p, [type]: true }));
    try {
      const body = test ? { test_email: testEmail } : {};
      const data = await fetchJson(`/admin/wake-up/${type}`, { method: 'POST', body: JSON.stringify(body) });
      setWakeupResult(p => ({ ...p, [type]: data }));
    } catch(e) {
      setWakeupResult(p => ({ ...p, [type]: { error: e.message } }));
    } finally {
      setWakeupLoading(p => ({ ...p, [type]: false }));
    }
  }
  const [quickChecks, setQuickChecks] = useState([]);
  const [msg, setMsg] = useState('');
  const [msgErr, setMsgErr] = useState(false);
  const [qUsers, setQUsers] = useState('');
  const [qFreelancers, setQFreelancers] = useState('');
  const [qAlerts, setQAlerts] = useState('');

  const flash = (text, err = false) => { setMsg(text); setMsgErr(err); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pend, qc] = await Promise.all([
        fetchJson('/admin/overview'),
        fetchJson('/admin/pending'),
        fetchJson('/admin/debug/quick'),
      ]);
      setOverview(ov);
      setPending(Array.isArray(pend) ? pend : []);
      setQuickChecks(Array.isArray(qc?.checks) ? qc.checks : []);
    } catch (e) {
      flash('Errore caricamento dati', true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    const data = await fetchJson(`/admin/users?q=${encodeURIComponent(qUsers)}`);
    setUsers(Array.isArray(data) ? data : []);
  }, [qUsers]);

  const loadFreelancers = useCallback(async () => {
    const data = await fetchJson(`/admin/freelancers?q=${encodeURIComponent(qFreelancers)}`);
    setFreelancers(Array.isArray(data) ? data : []);
  }, [qFreelancers]);

  const loadAlerts = useCallback(async () => {
    const data = await fetchJson(`/admin/alerts?q=${encodeURIComponent(qAlerts)}&limit=50`);
    setAlerts(Array.isArray(data?.items) ? data.items : []);
  }, [qAlerts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'coordinatori') loadUsers(); }, [tab, loadUsers]);
  useEffect(() => { if (tab === 'freelance') loadFreelancers(); }, [tab, loadFreelancers]);
  useEffect(() => { if (tab === 'alerts') loadAlerts(); }, [tab, loadAlerts]);

  // ── Catalogo v2: debounce ricerca programmi (400ms) ──
  useEffect(() => {
    if (tab !== 'catalogo') return;
    const timer = setTimeout(async () => {
      setCatProgrammiLoading(true);
      // Reset accordion quando cambia la query
      setCatExpandedProg(null);
      setCatNewEdProgId(null);
      try {
        const data = await fetchJson(`/admin/catalogo/programmi?q=${encodeURIComponent(catQProgrammi)}&page=1`);
        setCatProgrammi(
          Array.isArray(data.programmi)
            ? data.programmi.map(p => ({ ...p, edizioni_count: p.edizioni_count ?? p.edition_count ?? 0 }))
            : []
        );
        setCatProgrammiPage(data.page || 1);
        setCatProgrammiPages(data.pages || Math.ceil((data.total || 0) / (data.limit || 20)) || 1);
        setCatProgrammiTotal(data.total || 0);
      } catch(e) { /* silent */ }
      finally { setCatProgrammiLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [catQProgrammi, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Azioni coordinatori ──
  const approve = async (id) => {
    await fetchJson(`/admin/approve/${id}`, { method: 'POST' });
    flash('Coordinatore approvato — email inviata');
    setPending(p => p.filter(u => u.id !== id));
    load();
  };

  const sendWelcome = async (id, nome) => {
    try {
      await fetchJson(`/admin/welcome/${id}`, { method: 'POST' });
      flash(`Email di benvenuto inviata a ${nome || 'coordinatore'}`);
    } catch (e) {
      flash('Errore invio email');
    }
  };

  const reject = async (id) => {
    if (!window.confirm('Confermi il rifiuto? L\'utente verrà eliminato.')) return;
    await fetchJson(`/admin/reject/${id}`, { method: 'POST' });
    flash('Coordinatore rifiutato');
    setPending(p => p.filter(u => u.id !== id));
    load();
  };

  const patchUser = async (id, body) => {
    try {
      await fetchJson(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
      flash('Aggiornato');
      loadUsers();
    } catch (e) {
      flash('Errore aggiornamento', true);
    }
  };

  const patchFreelancer = async (id, body) => {
    try {
      await fetchJson(`/admin/freelancers/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
      flash('Aggiornato');
      loadFreelancers();
    } catch (e) {
      flash('Errore aggiornamento', true);
    }
  };

  // ── Azioni alert ──
  const saveAlert = async (id) => {
    const item = alerts.find(a => a.id === id);
    if (!item) return;
    try {
      await fetchJson(`/admin/alerts/${id}`, { method: 'PATCH', body: JSON.stringify({ title: item.title, category: item.category, description: item.description }) });
      flash('Alert aggiornato');
    } catch (e) {
      flash('Errore salvataggio', true);
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm(`Elimini alert #${id}?`)) return;
    await fetchJson(`/admin/alerts/${id}`, { method: 'DELETE' });
    setAlerts(a => a.filter(x => x.id !== id));
    flash('Alert eliminato');
  };

  // ── Azioni catalogo v2 ──

  async function doLoadCatProgrammi(q, page) {
    setCatProgrammiLoading(true);
    try {
      const data = await fetchJson(`/admin/catalogo/programmi?q=${encodeURIComponent(q)}&page=${page}`);
      setCatProgrammi(
        Array.isArray(data.programmi)
          ? data.programmi.map(p => ({ ...p, edizioni_count: p.edizioni_count ?? p.edition_count ?? 0 }))
          : []
      );
      setCatProgrammiPage(data.page || 1);
      setCatProgrammiPages(data.pages || Math.ceil((data.total || 0) / (data.limit || 20)) || 1);
      setCatProgrammiTotal(data.total || 0);
    } catch(e) { /* silent */ }
    finally { setCatProgrammiLoading(false); }
  }

  // Carica edizioni per un programma (lazy, una volta sola — poi usa la cache)
  async function loadEdizioniForProg(progId) {
    if (catEdizioniCache[progId] !== undefined) return; // già in cache
    setCatEdLoadingMap(prev => ({ ...prev, [progId]: true }));
    try {
      const data = await fetchJson(`/admin/catalogo/edizioni?program_id=${progId}`);
      setCatEdizioniCache(prev => ({
        ...prev,
        [progId]: Array.isArray(data.edizioni) ? data.edizioni : [],
      }));
    } catch(e) { /* silent */ }
    finally {
      setCatEdLoadingMap(prev => ({ ...prev, [progId]: false }));
    }
  }

  async function saveCatProgram(id) {
    try {
      await fetchJson(`/admin/catalogo/programmi/${id}`, { method: 'PATCH', body: JSON.stringify(catEditProg) });
      setCatProgMsg(p => ({ ...p, [id]: { text: 'Salvato ✓', error: false } }));
      setTimeout(() => setCatProgMsg(p => ({ ...p, [id]: null })), 2000);
      doLoadCatProgrammi(catQProgrammi, catProgrammiPage);
    } catch(e) {
      setCatProgMsg(p => ({ ...p, [id]: { text: `Errore: ${e.message}`, error: true } }));
    }
  }

  async function deleteCatProgram(id, nome) {
    if (!window.confirm(`Eliminare "${nome || `#${id}`}"?`)) return;
    try {
      await fetchJson(`/admin/catalogo/programmi/${id}`, { method: 'DELETE' });
      setCatExpandedProg(null);
      // Rimuove la cache edizioni del programma eliminato
      setCatEdizioniCache(prev => { const n = { ...prev }; delete n[id]; return n; });
      doLoadCatProgrammi(catQProgrammi, catProgrammiPage);
    } catch(e) {
      setCatProgMsg(p => ({ ...p, [id]: { text: `Errore: ${e.message}`, error: true } }));
    }
  }

  // Salva modifica edizione — applica logica numero puro → edition_no
  async function saveCatEdizione(progId, edId) {
    const val = (catEditEd.edition_label || '').trim();
    const isPureNumber = /^\d+$/.test(val);
    const edition_label = val;
    const edition_no = isPureNumber ? parseInt(val, 10) : null;

    const body = {
      edition_label,
      edition_no,
      year: catEditEd.year === '' ? null : (catEditEd.year != null ? Number(catEditEd.year) : null),
      window_start: catEditEd.window_start || null,
      window_end: catEditEd.window_end || null,
    };

    try {
      const data = await fetchJson(`/admin/catalogo/edizioni/${edId}`, { method: 'PATCH', body: JSON.stringify(body) });
      setCatEdMsg(p => ({ ...p, [edId]: { text: 'Salvata ✓', error: false } }));
      setTimeout(() => setCatEdMsg(p => ({ ...p, [edId]: null })), 2000);
      // Aggiorna cache locale
      if (data.edizione) {
        setCatEdizioniCache(prev => ({
          ...prev,
          [progId]: (prev[progId] || []).map(e => e.id === edId ? { ...e, ...data.edizione } : e),
        }));
      }
    } catch(e) {
      setCatEdMsg(p => ({ ...p, [edId]: { text: `Errore: ${e.message}`, error: true } }));
    }
  }

  // Elimina edizione — rimuove dalla cache locale
  async function deleteCatEdizione(progId, edId) {
    if (!window.confirm(`Eliminare definitivamente l'edizione #${edId}?`)) return;
    try {
      await fetchJson(`/admin/catalogo/edizioni/${edId}`, { method: 'DELETE' });
      setCatExpandedEd(null);
      setCatEdizioniCache(prev => ({
        ...prev,
        [progId]: (prev[progId] || []).filter(e => e.id !== edId),
      }));
      // Decrementa contatore nel programma
      setCatProgrammi(prev => prev.map(p =>
        p.id === progId ? { ...p, edizioni_count: Math.max(0, (p.edizioni_count || 0) - 1) } : p
      ));
    } catch(e) {
      setCatEdMsg(p => ({ ...p, [edId]: { text: `Errore: ${e.message}`, error: true } }));
    }
  }

  // Aggiungi nuova edizione — POST + aggiorna cache locale
  async function addCatEdizione(progId) {
    const val = (catNewEdForm.edition_label || '').trim();
    if (!val) return;
    const isPureNumber = /^\d+$/.test(val);
    const edition_label = val;
    const edition_no = isPureNumber ? parseInt(val, 10) : null;

    setCatNewEdSaving(true);
    setCatNewEdMsg(null);
    try {
      const body = {
        program_id: progId,
        edition_label,
        edition_no,
        year: catNewEdForm.year === '' ? null : (catNewEdForm.year ? Number(catNewEdForm.year) : null),
        window_start: catNewEdForm.window_start || null,
        window_end: catNewEdForm.window_end || null,
      };
      const nuova = await fetchJson('/admin/catalogo/edizioni', { method: 'POST', body: JSON.stringify(body) });
      // Prepend nella cache
      setCatEdizioniCache(prev => ({
        ...prev,
        [progId]: [nuova, ...(prev[progId] || [])],
      }));
      // Incrementa contatore nel programma
      setCatProgrammi(prev => prev.map(p =>
        p.id === progId ? { ...p, edizioni_count: (p.edizioni_count || 0) + 1 } : p
      ));
      // Chiude form
      setCatNewEdProgId(null);
      setCatNewEdForm({ edition_label: '', year: '', window_start: '', window_end: '' });
    } catch(e) {
      setCatNewEdMsg({ text: `Errore: ${e.message}`, error: true });
    } finally {
      setCatNewEdSaving(false);
    }
  }

  if (loading) return (
    <div style={{ ...S.page, display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <div style={{ color: '#d4a550', fontFamily: 'Kanit, sans-serif', letterSpacing: '0.2em' }}>CARICAMENTO...</div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.logo}>JKL</div>
        <h1 style={S.title}>Admin Dashboard</h1>
        <p style={S.subtitle}>Pannello riservato — accesso ristretto</p>

        {/* KPI */}
        <div style={S.grid}>
          {[
            { label: 'Utenti totali', value: overview?.totals?.users ?? 0 },
            { label: 'Freelance visibili', value: overview?.totals?.freelancers ?? 0 },
            { label: 'Coordinatori', value: overview?.totals?.coordinators ?? 0 },
            { label: 'In attesa approv.', value: overview?.totals?.pendingCoordinators ?? 0, warn: true },
            { label: 'Nuovi 7gg', value: overview?.access?.registrationsLast7d ?? 0 },
            { label: 'Nuovi 30gg', value: overview?.access?.registrationsLast30d ?? 0 },
            { label: 'Chat attivi 7gg', value: overview?.access?.activeMessageSendersLast7d ?? 0 },
          ].map(({ label, value, warn }) => (
            <div key={label} style={{ ...S.card, borderColor: warn && value > 0 ? 'rgba(212,165,80,0.3)' : undefined }}>
              <div style={S.kpiLabel}>{label}</div>
              <div style={{ ...S.kpiValue, color: warn && value > 0 ? '#f0c878' : '#d4a550' }}>{value}</div>
            </div>
          ))}
          {/* Sezione addormentati */}
          <div style={{ ...S.card, gridColumn: '1 / -1', background: 'rgba(212,165,80,0.04)', borderColor: 'rgba(212,165,80,0.15)' }}>
            <div style={{ ...S.kpiLabel, marginBottom: 12 }}>⚠ Profili da svegliare</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'Senza ruolo', value: overview?.totals?.noRole ?? 0 },
                { label: 'Senza produzioni', value: overview?.totals?.noProductions ?? 0 },
                { label: 'Invisibili', value: overview?.totals?.invisible ?? 0 },
                { label: 'Senza città', value: overview?.totals?.noCity ?? 0 },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: value > 0 ? '#f0c878' : '#d4a550' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Per azienda */}
          {overview?.totals?.byCompany?.length > 0 && (
            <div style={{ ...S.card, gridColumn: '1 / -1' }}>
              <div style={{ ...S.kpiLabel, marginBottom: 12 }}>Coordinatori per azienda</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {overview.totals.byCompany.map(({ company, n }) => (
                  <div key={company} style={{ fontSize: 13 }}>
                    <span style={{ color: '#d4a550', fontWeight: 700 }}>{n}</span>
                    <span style={{ color: '#7a7f96', marginLeft: 6 }}>{company}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* TABS */}
        <div style={S.tabs}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'wakeup', label: '⚡ Sveglia' },
            { id: 'newsletter', label: '📨 Newsletter' },
            { id: 'pending', label: `In attesa${pending.length > 0 ? ` (${pending.length})` : ''}` },
            { id: 'coordinatori', label: 'Coordinatori' },
            { id: 'freelance', label: 'Freelance' },
            { id: 'alerts', label: 'Alert' },
            { id: 'cv_catalog', label: t('admin_cv.tab_label') },
            { id: 'catalogo', label: 'Catalogo' },
            { id: 'debug', label: 'Debug' },
          ].map(t => (
            <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Riepilogo sistema</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <div style={{ ...S.kpiLabel, marginBottom: '12px' }}>Accessi e attività</div>
                {[
                  ['Registrazioni ultimi 30gg', overview?.access?.registrationsLast30d ?? 0],
                  ['Mittenti messaggi attivi (7gg)', overview?.access?.activeMessageSendersLast7d ?? 0],
                  ['Alert inviati (7gg)', overview?.alerts?.recent7d ?? 0],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ color: '#b0adb8', fontSize: '13px' }}>{k}</span>
                    <span style={{ fontWeight: 700, color: '#f0ece4' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ ...S.kpiLabel, marginBottom: '12px' }}>Google Analytics</div>
                <span style={S.badge('blue')}>G-YGXG5NEPYH</span>
                <div style={{ ...S.small, marginTop: '6px' }}>Attivo via cookie consent</div>
                {overview?.alerts?.byCategory?.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ ...S.kpiLabel, marginBottom: '8px' }}>Top ruoli alert</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {overview.alerts.byCategory.map(c => (
                        <span key={c.category} style={S.badge('blue')}>{c.category}: {c.n}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PENDING */}
        {tab === 'newsletter' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={S.section}>
              <div style={S.sectionTitle}>📨 Nuova comunicazione</div>

              {/* Segmento */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Destinatari</div>
                <select value={nlSegment} onChange={e => setNlSegment(e.target.value)}
                  style={{ ...S.input, width: '100%', colorScheme: 'dark' }}>
                  <option value="all_freelance">Tutti i freelance</option>
                  <option value="all_coordinators">Tutti i coordinatori</option>
                  <option value="all">Tutti gli utenti</option>
                  <option value="no_role">Freelance senza ruolo</option>
                  <option value="no_productions">Freelance senza produzioni</option>
                  <option value="invisible">Freelance invisibili</option>
                  <option value="no_city">Freelance senza città</option>
                </select>
              </div>

              {/* Oggetto */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Oggetto</div>
                <input value={nlSubject} onChange={e => setNlSubject(e.target.value)}
                  placeholder="Oggetto dell'email..."
                  style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} />
              </div>

              {/* Corpo */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Testo</div>
                <textarea value={nlBody} onChange={e => setNlBody(e.target.value)}
                  placeholder="Scrivi il testo. Doppio invio = nuovo paragrafo. ## Titolo = titolo dorato."
                  rows={8}
                  style={{ ...S.input, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
              </div>

              {/* Immagine opzionale */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Immagine (opzionale)</div>
                {nlImageUrl && (
                  <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
                    <img src={`https://joinjkl.com${nlImageUrl}`} alt="" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, display: 'block' }} />
                    <button onClick={() => setNlImageUrl('')} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '2px 6px', fontSize: 12 }}>×</button>
                  </div>
                )}
                <label style={{ display: 'inline-block', cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#a0a0b0' }}>
                  {nlImageUploading ? 'Caricamento...' : nlImageUrl ? '📎 Cambia immagine' : '📎 Carica immagine'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setNlImageUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('image', file);
                      const token = localStorage.getItem('token') || '';
                      const res = await fetch('/api/admin/newsletter/upload-image', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                      const data = await res.json();
                      if (data.url) setNlImageUrl(data.url);
                    } catch(e) { alert('Errore upload: ' + e.message); }
                    finally { setNlImageUploading(false); }
                  }} />
                </label>
              </div>

              {/* Test */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>🧪 Test prima di inviare</div>
                <div style={S.row}>
                  <input value={nlTestEmail} onChange={e => setNlTestEmail(e.target.value)}
                    placeholder="tua@email.com"
                    style={{ ...S.input, flex: 1 }} />
                  <button style={S.btnGhost} onClick={() => sendNewsletter(true)} disabled={!nlTestEmail || !nlSubject || !nlBody || nlLoading}>
                    Invia test
                  </button>
                </div>
              </div>

              {/* Invia a email specifica */}
              <div style={{ background: 'rgba(212,165,80,0.04)', border: '1px solid rgba(212,165,80,0.2)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#d4a550', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>📧 Invia a utente specifico</div>
                <div style={S.row}>
                  <input value={nlTestEmail} onChange={e => setNlTestEmail(e.target.value)}
                    placeholder="email@utente.com"
                    style={{ ...S.input, flex: 1 }} />
                  <button style={{ ...S.btnGhost, borderColor: 'rgba(212,165,80,0.3)', color: '#d4a550' }}
                    onClick={async () => {
                      if (!nlTestEmail || !nlSubject || !nlBody) return;
                      if (!window.confirm(`Inviare a ${nlTestEmail}?`)) return;
                      try {
                        setNlLoading(true);
                        const body = { subject: nlSubject, body: nlBody, segment: 'specific', specific_email: nlTestEmail, image_url: nlImageUrl || undefined };
                        const data = await fetchJson('/admin/newsletter/send', { method: 'POST', body: JSON.stringify(body) });
                        alert(`✅ Inviata a ${nlTestEmail}`);
                      } catch(e) { alert('Errore: ' + e.message); } finally { setNlLoading(false); }
                    }}
                    disabled={!nlTestEmail || !nlSubject || !nlBody || nlLoading}>
                    Invia a questa email
                  </button>
                </div>
              </div>

              {/* Invio */}
              <div style={S.row}>
                <button style={{ ...S.btn, opacity: (!nlSubject || !nlBody) ? 0.4 : 1 }}
                  onClick={() => { if(window.confirm('Sei sicuro di voler inviare questa comunicazione?')) sendNewsletter(false); }}
                  disabled={!nlSubject || !nlBody || nlLoading}>
                  {nlLoading ? 'Invio in corso...' : '📨 Invia a tutti'}
                </button>
              </div>

              {nlResult && (
                <div style={{ marginTop: 12, fontSize: 13, color: nlResult.error ? '#ff8484' : '#67df95' }}>
                  {nlResult.error ? `Errore: ${nlResult.error}` :
                   nlResult.mode === 'test' ? '✓ Email di test inviata correttamente' :
                   `✓ Inviati: ${nlResult.sent} | Falliti: ${nlResult.failed} | Totale: ${nlResult.total}`}
                </div>
              )}
            </div>
          </div>
        )}
        {tab === 'wakeup' && (
          <div>
            <div style={{ ...S.section, marginBottom: 16 }}>
              <div style={S.sectionTitle}>📧 Email di test</div>
              <div style={S.row}>
                <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="tua@email.com" style={{ ...S.input, width: 280 }} />
                <span style={{ fontSize: 12, color: '#7a7f96' }}>Invia a questa email per vedere la resa prima del lancio</span>
              </div>
            </div>
            {[
              { type: 'no_role', label: 'Senza ruolo', count: overview?.totals?.noRole ?? 0, desc: 'Freelance registrati che non hanno ancora inserito il loro ruolo professionale. Senza ruolo non sono cercabili.' },
              { type: 'no_productions', label: 'Senza produzioni', count: overview?.totals?.noProductions ?? 0, desc: 'Freelance che non hanno inserito nessuna produzione nel loro storico. Il curriculum è vuoto.' },
              { type: 'invisible', label: 'Invisibili', count: overview?.totals?.invisible ?? 0, desc: 'Freelance che hanno disattivato la visibilità del profilo. Non vengono trovati nelle ricerche.' },
              { type: 'no_city', label: 'Senza città', count: overview?.totals?.noCity ?? 0, desc: 'Freelance che non hanno ancora indicato le loro zone di lavoro. Non vengono trovati nelle ricerche per città.' },
            ].map(({ type, label, count, desc }) => (
              <div key={type} style={S.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={S.sectionTitle}>{label}</div>
                    <div style={{ fontSize: 13, color: '#7a7f96', marginBottom: 8 }}>{desc}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: count > 0 ? '#f0c878' : '#67df95' }}>{count} <span style={{ fontSize: 13, fontWeight: 400, color: '#7a7f96' }}>utenti</span></div>
                  </div>
                </div>
                <div style={S.row}>
                  <button style={S.btnGhost} onClick={() => sendWakeup(type, true)} disabled={!testEmail || wakeupLoading[type]}>
                    {wakeupLoading[type] ? 'Invio...' : '🧪 Test'}
                  </button>
                  <button style={{ ...S.btn, opacity: count === 0 ? 0.4 : 1 }} onClick={() => { if(window.confirm(`Inviare email a ${count} utenti?`)) sendWakeup(type, false); }} disabled={count === 0 || wakeupLoading[type]}>
                    {wakeupLoading[type] ? 'Invio in corso...' : `📨 Invia a tutti (${count})`}
                  </button>
                </div>
                {wakeupResult[type] && (
                  <div style={{ marginTop: 8, fontSize: 12, color: wakeupResult[type].error ? '#ff8484' : '#67df95' }}>
                    {wakeupResult[type].error ? `Errore: ${wakeupResult[type].error}` : wakeupResult[type].mode === 'test' ? '✓ Email di test inviata' : `✓ Inviati: ${wakeupResult[type].sent} | Falliti: ${wakeupResult[type].failed}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {tab === 'pending' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Coordinatori in attesa di approvazione</div>
            {pending.length === 0 ? (
              <p style={{ color: '#7a7f96', fontSize: '13px' }}>Nessun coordinatore in attesa.</p>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Nome</th>
                    <th style={S.th}>Email</th>
                    <th style={S.th}>Registrato il</th>
                    <th style={S.th}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map(u => (
                    <tr key={u.id}>
                      <td style={S.td}>{u.nome} {u.cognome}</td>
                      <td style={S.td}>{u.email}</td>
                      <td style={S.td} ><span style={S.small}>{new Date(u.created_at).toLocaleDateString('it-IT')}</span></td>
                      <td style={S.td}>
                        <div style={S.row}>
                          <button style={S.btnSuccess} onClick={() => approve(u.id)}>✓ Approva</button>
                          <button style={S.btnDanger} onClick={() => reject(u.id)}>✕ Rifiuta</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* COORDINATORI */}
        {tab === 'coordinatori' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Gestione coordinatori</div>
            <div style={S.row}>
              <input style={{ ...S.input, minWidth: '260px' }} placeholder="Cerca per nome/email..." value={qUsers} onChange={e => setQUsers(e.target.value)} />
              <button style={S.btn} onClick={loadUsers}>Cerca</button>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nome</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Azienda</th>
                  <th style={S.th}>Ruolo</th>
                  <th style={S.th}>Stato</th>
                  <th style={S.th}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={S.td}>{u.nome} {u.cognome}</td>
                    <td style={S.td}><span style={S.small}>{u.email}</span></td>
                    <td style={S.td}>{u.company_name || <span style={S.small}>—</span>}</td>
                    <td style={S.td}>
                      <span style={S.badge(u.company_role === 'supervisor' ? 'gold' : 'blue')}>
                        {u.company_role || 'coordinator'}
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(u.approved ? 'green' : 'red')}>
                        {u.approved ? 'Approvato' : 'In attesa'}
                      </span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {u.company_role !== 'supervisor'
                          ? <button style={S.btnGhost} onClick={() => patchUser(u.id, { company_role: 'supervisor' })}>↑ Promuovi</button>
                          : <button style={S.btnGhost} onClick={() => patchUser(u.id, { company_role: 'coordinator' })}>↓ Degrada</button>
                        }
                        {u.approved
                          ? <button style={S.btnDanger} onClick={() => patchUser(u.id, { approved: false })}>Sospendi</button>
                          : <button style={S.btnSuccess} onClick={() => approve(u.id)}>Approva</button>
                        }
                        {u.approved && (
                          <button style={S.btnGhost} onClick={() => sendWelcome(u.id, u.nome)}>✉ Benvenuto</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td style={S.td} colSpan={6}><span style={S.small}>Nessun risultato</span></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* FREELANCE */}
        {tab === 'freelance' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Gestione freelance</div>
            <div style={S.row}>
              <input style={{ ...S.input, minWidth: '260px' }} placeholder="Cerca per nome/email..." value={qFreelancers} onChange={e => setQFreelancers(e.target.value)} />
              <button style={S.btn} onClick={loadFreelancers}>Cerca</button>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nome</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Ruoli</th>
                  <th style={S.th}>Visibile</th>
                  <th style={S.th}>Iscritto il</th>
                  <th style={S.th}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {freelancers.map(u => (
                  <tr key={u.id}>
                    <td style={S.td}>{u.nome} {u.cognome}</td>
                    <td style={S.td}><span style={S.small}>{u.email}</span></td>
                    <td style={S.td}><span style={S.small}>{(u.ruoli || []).slice(0, 2).join(', ') || '—'}</span></td>
                    <td style={S.td}>
                      <span style={S.badge(u.visible ? 'green' : 'red')}>
                        {u.visible ? 'Visibile' : 'Nascosto'}
                      </span>
                    </td>
                    <td style={S.td}><span style={S.small}>{new Date(u.created_at).toLocaleDateString('it-IT')}</span></td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {u.visible
                          ? <button style={S.btnDanger} onClick={() => patchFreelancer(u.id, { visible: false })}>Nascondi</button>
                          : <button style={S.btnSuccess} onClick={() => patchFreelancer(u.id, { visible: true })}>Rendi visibile</button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
                {freelancers.length === 0 && (
                  <tr><td style={S.td} colSpan={6}><span style={S.small}>Nessun risultato</span></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ALERTS */}
        {tab === 'alerts' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Gestione alert</div>
            <div style={S.row}>
              <input style={{ ...S.input, minWidth: '260px' }} placeholder="Cerca alert..." value={qAlerts} onChange={e => setQAlerts(e.target.value)} />
              <button style={S.btn} onClick={loadAlerts}>Cerca</button>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>ID</th>
                  <th style={S.th}>Titolo</th>
                  <th style={S.th}>Ruolo</th>
                  <th style={S.th}>Testo alert</th>
                  <th style={S.th}>Data</th>
                  <th style={S.th}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map(a => (
                  <tr key={a.id}>
                    <td style={S.td}><span style={S.small}>#{a.id}</span></td>
                    <td style={S.td}>
                      <input
                        style={{ ...S.input, minWidth: '280px', fontSize: '13px' }}
                        value={a.title || ''}
                        onChange={e => setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, title: e.target.value } : x))}
                      />
                    </td>
                    <td style={S.td}><span style={S.badge('blue')}>{a.category}</span></td>
                    <td style={S.td}>
                      <textarea
                        style={{ ...S.input, minWidth: '320px', minHeight: '80px', resize: 'vertical', fontSize: '12px' }}
                        value={a.description || ''}
                        onChange={e => setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, description: e.target.value } : x))}
                      />
                    </td>
                    <td style={S.td}><span style={S.small}>{new Date(a.created_at).toLocaleDateString('it-IT')}</span></td>
                    <td style={S.td}>
                      <button style={S.btn} onClick={() => saveAlert(a.id)}>Salva</button>
                      <button style={S.btnDanger} onClick={() => deleteAlert(a.id)}>Elimina</button>
                    </td>
                  </tr>
                ))}
                {alerts.length === 0 && (
                  <tr><td style={S.td} colSpan={6}><span style={S.small}>Nessun alert</span></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* CATALOGO CV */}
        {tab === 'cv_catalog' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>{t('admin_cv.title')}</div>
            <p style={{ color: '#7a7f96', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
              {t('admin_cv.subtitle')}
            </p>
            <CvCatalogAdmin />
          </div>
        )}

        {/* ─── GESTIONE CATALOGO v2 ─────────────────────────────────────────── */}
        {tab === 'catalogo' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Gestione Catalogo</div>

            {/* Ricerca programmi */}
            <div style={S.row}>
              <input
                style={{ ...S.input, minWidth: '300px' }}
                placeholder="Cerca per nome o slug..."
                value={catQProgrammi}
                onChange={e => setCatQProgrammi(e.target.value)}
              />
              {catProgrammiLoading && <span style={S.small}>Caricamento...</span>}
              {!catProgrammiLoading && catProgrammiTotal > 0 && (
                <span style={S.small}>{catProgrammiTotal} programmi</span>
              )}
            </div>

            {/* Lista programmi */}
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nome</th>
                  <th style={S.th}>Slug</th>
                  <th style={S.th}>Stato</th>
                  <th style={S.th}>Edizioni</th>
                  <th style={{ ...S.th, width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {catProgrammi.map(prog => (
                  <React.Fragment key={prog.id}>
                    {/* ── Riga programma ── */}
                    <tr
                      style={{
                        cursor: 'pointer',
                        background: catExpandedProg === prog.id ? 'rgba(212,165,80,0.04)' : undefined,
                      }}
                      onClick={() => {
                        if (catExpandedProg === prog.id) {
                          setCatExpandedProg(null);
                        } else {
                          setCatExpandedProg(prog.id);
                          setCatEditProg({ name: prog.name, is_verified: prog.is_verified });
                          setCatProgMsg(p => ({ ...p, [prog.id]: null }));
                          setCatExpandedEd(null);
                          setCatNewEdProgId(null);
                          // Caricamento lazy edizioni (solo se non già in cache)
                          loadEdizioniForProg(prog.id);
                        }
                      }}
                    >
                      <td style={S.td}>{prog.name}</td>
                      <td style={S.td}><span style={S.small}>{prog.slug}</span></td>
                      <td style={S.td}>
                        {prog.is_verified
                          ? <span style={S.badge('gold')}>✓ Verificato</span>
                          : <span style={{ ...S.badge('blue'), color: '#7a7f96', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>Non verif.</span>
                        }
                      </td>
                      <td style={S.td}>
                        <span style={S.small}>{prog.edizioni_count ?? 0} edizioni</span>
                      </td>
                      <td style={{ ...S.td, color: '#7a7f96', fontSize: 11, textAlign: 'right' }}>
                        {catExpandedProg === prog.id ? '▲' : '▼'}
                      </td>
                    </tr>

                    {/* ── Accordion body ── */}
                    {catExpandedProg === prog.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{
                            background: 'rgba(212,165,80,0.04)',
                            borderLeft: '3px solid rgba(212,165,80,0.35)',
                            padding: '18px 20px',
                          }}>

                            {/* i) EDIT PROGRAMMA */}
                            <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Modifica programma</div>
                              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 5 }}>Nome</div>
                                  <input
                                    style={{ ...S.input, minWidth: '280px' }}
                                    value={catEditProg.name || ''}
                                    onChange={e => setCatEditProg(p => ({ ...p, name: e.target.value }))}
                                    onClick={ev => ev.stopPropagation()}
                                  />
                                </div>
                                <label
                                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#ecedf4', cursor: 'pointer', paddingBottom: 2 }}
                                  onClick={ev => ev.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!catEditProg.is_verified}
                                    onChange={e => setCatEditProg(p => ({ ...p, is_verified: e.target.checked }))}
                                  />
                                  Verificato
                                </label>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <button
                                  style={S.btn}
                                  onClick={ev => { ev.stopPropagation(); saveCatProgram(prog.id); }}
                                >
                                  Salva modifiche
                                </button>
                                <button
                                  style={{
                                    ...S.btnDanger,
                                    opacity: (prog.edizioni_count ?? 0) > 0 ? 0.4 : 1,
                                    cursor: (prog.edizioni_count ?? 0) > 0 ? 'not-allowed' : 'pointer',
                                  }}
                                  title={(prog.edizioni_count ?? 0) > 0
                                    ? `Ha ${prog.edizioni_count} edizioni collegate`
                                    : 'Elimina programma'}
                                  disabled={(prog.edizioni_count ?? 0) > 0}
                                  onClick={ev => { ev.stopPropagation(); deleteCatProgram(prog.id, prog.name); }}
                                >
                                  Elimina programma
                                </button>
                              </div>
                              {catProgMsg[prog.id] && (
                                <div style={{ fontSize: 12, marginTop: 10, color: catProgMsg[prog.id].error ? '#ff8484' : '#67df95' }}>
                                  {catProgMsg[prog.id].text}
                                </div>
                              )}
                            </div>

                            {/* ii) LISTA EDIZIONI */}
                            <div>
                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Edizioni ({catEdizioniCache[prog.id]?.length ?? '…'})
                              </div>

                              {catEdLoadingMap[prog.id] && (
                                <div style={{ ...S.small, marginBottom: 10 }}>Caricamento edizioni...</div>
                              )}

                              {!catEdLoadingMap[prog.id] && catEdizioniCache[prog.id] && (
                                <div style={{ marginBottom: 12 }}>
                                  {catEdizioniCache[prog.id].map(ed => (
                                    <div key={ed.id} style={{ marginBottom: 4 }}>
                                      {/* Riga edizione */}
                                      <div
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 12,
                                          padding: '8px 12px',
                                          background: catExpandedEd === ed.id
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(255,255,255,0.02)',
                                          border: `1px solid ${catExpandedEd === ed.id ? 'rgba(212,165,80,0.2)' : 'rgba(255,255,255,0.07)'}`,
                                          borderRadius: catExpandedEd === ed.id ? '6px 6px 0 0' : '6px',
                                          cursor: 'pointer',
                                        }}
                                        onClick={ev => {
                                          ev.stopPropagation();
                                          if (catExpandedEd === ed.id) {
                                            setCatExpandedEd(null);
                                          } else {
                                            setCatExpandedEd(ed.id);
                                            setCatEditEd({
                                              edition_label: ed.edition_label || '',
                                              year: ed.year ?? '',
                                              window_start: ed.window_start ? ed.window_start.slice(0, 10) : '',
                                              window_end: ed.window_end ? ed.window_end.slice(0, 10) : '',
                                            });
                                            setCatEdMsg(p => ({ ...p, [ed.id]: null }));
                                          }
                                        }}
                                      >
                                        <span style={{ fontSize: 13, flex: 1, fontWeight: 500 }}>
                                          {ed.edition_label || <span style={S.small}>—</span>}
                                        </span>
                                        {ed.year != null && (
                                          <span style={S.small}>{ed.year}</span>
                                        )}
                                        {(ed.window_start || ed.window_end) && (
                                          <span style={S.small}>
                                            {ed.window_start?.slice(0, 10) || '?'} – {ed.window_end?.slice(0, 10) || '?'}
                                          </span>
                                        )}
                                        <span style={{ color: '#7a7f96', fontSize: 11 }}>
                                          {catExpandedEd === ed.id ? '▲' : '▼'}
                                        </span>
                                      </div>

                                      {/* Mini-form edizione */}
                                      {catExpandedEd === ed.id && (
                                        <div style={{
                                          background: 'rgba(255,255,255,0.03)',
                                          border: '1px solid rgba(212,165,80,0.2)',
                                          borderTop: 'none',
                                          borderRadius: '0 0 6px 6px',
                                          borderLeft: '2px solid rgba(212,165,80,0.3)',
                                          padding: '12px 14px',
                                        }}>
                                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                                            <div>
                                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Stagione / Edizione</div>
                                              <input
                                                style={{ ...S.input, width: 210 }}
                                                value={catEditEd.edition_label}
                                                placeholder="Es. 13 oppure Stagione 13"
                                                onChange={e => setCatEditEd(p => ({ ...p, edition_label: e.target.value }))}
                                                onClick={ev => ev.stopPropagation()}
                                              />
                                            </div>
                                            <div>
                                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Anno</div>
                                              <input
                                                type="number"
                                                style={{ ...S.input, width: 90 }}
                                                value={catEditEd.year}
                                                onChange={e => setCatEditEd(p => ({ ...p, year: e.target.value }))}
                                                onClick={ev => ev.stopPropagation()}
                                              />
                                            </div>
                                            <div>
                                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Inizio finestra</div>
                                              <input
                                                type="date"
                                                style={{ ...S.input, width: 150, colorScheme: 'dark' }}
                                                value={catEditEd.window_start}
                                                onChange={e => setCatEditEd(p => ({ ...p, window_start: e.target.value }))}
                                                onClick={ev => ev.stopPropagation()}
                                              />
                                            </div>
                                            <div>
                                              <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Fine finestra</div>
                                              <input
                                                type="date"
                                                style={{ ...S.input, width: 150, colorScheme: 'dark' }}
                                                value={catEditEd.window_end}
                                                onChange={e => setCatEditEd(p => ({ ...p, window_end: e.target.value }))}
                                                onClick={ev => ev.stopPropagation()}
                                              />
                                            </div>
                                          </div>
                                          <div style={{ display: 'flex', gap: 8 }}>
                                            <button
                                              style={S.btn}
                                              onClick={ev => { ev.stopPropagation(); saveCatEdizione(prog.id, ed.id); }}
                                            >
                                              Salva
                                            </button>
                                            <button
                                              style={S.btnDanger}
                                              onClick={ev => { ev.stopPropagation(); deleteCatEdizione(prog.id, ed.id); }}
                                            >
                                              Elimina
                                            </button>
                                          </div>
                                          {catEdMsg[ed.id] && (
                                            <div style={{ fontSize: 12, marginTop: 8, color: catEdMsg[ed.id].error ? '#ff8484' : '#67df95' }}>
                                              {catEdMsg[ed.id].text}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}

                                  {catEdizioniCache[prog.id].length === 0 && (
                                    <div style={{ ...S.small, marginBottom: 8 }}>Nessuna edizione</div>
                                  )}
                                </div>
                              )}

                              {/* iii) AGGIUNGI EDIZIONE */}
                              <div style={{ marginTop: 8 }}>
                                {catNewEdProgId !== prog.id ? (
                                  <button
                                    style={S.btnGhost}
                                    onClick={ev => {
                                      ev.stopPropagation();
                                      setCatNewEdProgId(prog.id);
                                      setCatNewEdForm({ edition_label: '', year: '', window_start: '', window_end: '' });
                                      setCatNewEdMsg(null);
                                    }}
                                  >
                                    + Aggiungi edizione
                                  </button>
                                ) : (
                                  <div style={{
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: 8,
                                    padding: '14px 16px',
                                  }}>
                                    <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Nuova edizione</div>
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                                      <div>
                                        <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Stagione / Edizione *</div>
                                        <input
                                          style={{ ...S.input, width: 210 }}
                                          value={catNewEdForm.edition_label}
                                          placeholder="Es. 13 oppure Stagione 13"
                                          onChange={e => setCatNewEdForm(p => ({ ...p, edition_label: e.target.value }))}
                                          onClick={ev => ev.stopPropagation()}
                                          autoFocus
                                        />
                                      </div>
                                      <div>
                                        <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Anno</div>
                                        <input
                                          type="number"
                                          style={{ ...S.input, width: 90 }}
                                          value={catNewEdForm.year}
                                          onChange={e => setCatNewEdForm(p => ({ ...p, year: e.target.value }))}
                                          onClick={ev => ev.stopPropagation()}
                                        />
                                      </div>
                                      <div>
                                        <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Inizio finestra</div>
                                        <input
                                          type="date"
                                          style={{ ...S.input, width: 150, colorScheme: 'dark' }}
                                          value={catNewEdForm.window_start}
                                          onChange={e => setCatNewEdForm(p => ({ ...p, window_start: e.target.value }))}
                                          onClick={ev => ev.stopPropagation()}
                                        />
                                      </div>
                                      <div>
                                        <div style={{ fontSize: 11, color: '#7a7f96', marginBottom: 4 }}>Fine finestra</div>
                                        <input
                                          type="date"
                                          style={{ ...S.input, width: 150, colorScheme: 'dark' }}
                                          value={catNewEdForm.window_end}
                                          onChange={e => setCatNewEdForm(p => ({ ...p, window_end: e.target.value }))}
                                          onClick={ev => ev.stopPropagation()}
                                        />
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <button
                                        style={{
                                          ...S.btn,
                                          opacity: !catNewEdForm.edition_label.trim() || catNewEdSaving ? 0.5 : 1,
                                        }}
                                        disabled={!catNewEdForm.edition_label.trim() || catNewEdSaving}
                                        onClick={ev => { ev.stopPropagation(); addCatEdizione(prog.id); }}
                                      >
                                        {catNewEdSaving ? 'Salvataggio...' : 'Aggiungi'}
                                      </button>
                                      <button
                                        style={S.btnGhost}
                                        onClick={ev => { ev.stopPropagation(); setCatNewEdProgId(null); }}
                                      >
                                        Annulla
                                      </button>
                                    </div>
                                    {catNewEdMsg && (
                                      <div style={{ fontSize: 12, marginTop: 8, color: catNewEdMsg.error ? '#ff8484' : '#67df95' }}>
                                        {catNewEdMsg.text}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {catProgrammi.length === 0 && !catProgrammiLoading && (
                  <tr>
                    <td style={S.td} colSpan={5}><span style={S.small}>Nessun risultato</span></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Paginazione */}
            {catProgrammiPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button
                  style={{ ...S.btnGhost, opacity: catProgrammiPage <= 1 ? 0.4 : 1 }}
                  disabled={catProgrammiPage <= 1}
                  onClick={() => {
                    setCatExpandedProg(null);
                    setCatNewEdProgId(null);
                    doLoadCatProgrammi(catQProgrammi, catProgrammiPage - 1);
                  }}
                >
                  ← Prec
                </button>
                <span style={S.small}>Pagina {catProgrammiPage} di {catProgrammiPages}</span>
                <button
                  style={{ ...S.btnGhost, opacity: catProgrammiPage >= catProgrammiPages ? 0.4 : 1 }}
                  disabled={catProgrammiPage >= catProgrammiPages}
                  onClick={() => {
                    setCatExpandedProg(null);
                    setCatNewEdProgId(null);
                    doLoadCatProgrammi(catQProgrammi, catProgrammiPage + 1);
                  }}
                >
                  Succ →
                </button>
              </div>
            )}
          </div>
        )}

        {/* DEBUG */}
        {tab === 'debug' && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Debug sistema</div>
            <div style={S.row}>
              <button style={S.btnGhost} onClick={() => fetchJson('/admin/debug/quick').then(d => setQuickChecks(d?.checks || []))}>Aggiorna</button>
            </div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Check</th>
                  <th style={S.th}>Valore</th>
                  <th style={S.th}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {quickChecks.map(c => (
                  <tr key={c.key}>
                    <td style={S.td}>{c.label}</td>
                    <td style={S.td}>{c.value}</td>
                    <td style={S.td}>
                      <span style={S.badge(c.severity === 'ok' ? 'green' : c.severity === 'warning' ? 'gold' : 'blue')}>
                        {c.severity}
                      </span>
                    </td>
                  </tr>
                ))}
                {quickChecks.length === 0 && (
                  <tr><td style={S.td} colSpan={3}><span style={S.small}>Nessun dato</span></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Msg text={msg} error={msgErr} />
      </div>
    </div>
  );
}
